import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resolveNextPath } from './nextPath';

export function PasswordGate() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const navigate = useNavigate();
  // `AuthGuard` 拦下时把"原本要去的路由"放进 `?next=`（见 App.tsx）；默认落回书架
  const [searchParams] = useSearchParams();
  const nextPath = resolveNextPath(searchParams.get('next'));

  /** 登录成功后的统一落点：回到原处。用 replace，免得"后退"又退回登录页 */
  const goAfterLogin = useCallback(() => {
    sessionStorage.setItem('sw_authenticated', '1');
    navigate(nextPath, { replace: true });
  }, [navigate, nextPath]);

  useEffect(() => {
    fetch('/api/auth/password-status', { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => setHasPassword(d.hasPassword))
      .catch(() => setServiceUnavailable(true));
  }, []);

  /**
   * Cookie 还有效时**不要**再让人输一遍密码。
   *
   * `sw_session` 是**浏览器会话 Cookie**：同一次浏览里刷新页面、或再打开一次都还算
   * 登录中，不该重复输密码（也连带影响"刷新后还能不能答复那张审批卡"的体验）；
   * 而关掉浏览器之后 Cookie 已经没了，探测会 401，就正常要求输密码。
   */
  useEffect(() => {
    fetch('/api/auth/session', { credentials: 'include' })
      .then(r => {
        if (r.ok) goAfterLogin();
      })
      .catch(() => { /* 探测失败就让用户照常输密码 */ });
  }, [goAfterLogin]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!password) { setError('请输入密码'); return; }
    if (password.length < 4) { setError('密码长度至少4位'); return; }
    if (password !== confirmPassword) { setError('两次输入的密码不一致'); return; }

    setLoading(true);
    try {
      const r = await fetch('/api/auth/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      const d = await r.json();
      if (r.ok) {
        goAfterLogin();
      } else {
        setError(d.error || '设置失败');
      }
    } catch {
      setError('设置失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!password) { setError('请输入密码'); return; }

    setLoading(true);
    try {
      const result = await window.api.verifyPassword(password);
      if (result.success) {
        goAfterLogin();
      } else {
        setError(result.error || '密码错误');
      }
    } catch {
      setError('密码错误');
    } finally {
      setLoading(false);
    }
  };

  if (hasPassword === null && !serviceUnavailable) return null;

  const loginLogo = (
    <div className="login-logo" aria-hidden="true">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    </div>
  );

  if (serviceUnavailable) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ maxWidth: 440 }}>
          <div className="login-header">
            {loginLogo}
            <h1 className="login-title">小天书</h1>
            <p className="login-subtitle">个人写作工具</p>
          </div>
          <div style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <p style={{ fontSize: 48, margin: '0 0 16px' }}>&#9888;</p>
            <p style={{ fontWeight: 600, color: 'var(--text-color)', margin: '0 0 8px' }}>服务暂不可用</p>
            <p style={{ margin: 0 }}>无法连接到后端服务，请检查数据库是否已启动。</p>
            <p style={{ margin: '12px 0 0', fontSize: 12, opacity: 0.7 }}>启动后刷新页面即可恢复访问。</p>
          </div>
          <div className="login-icp">
            <div className="login-icp-row">
              <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer" className="login-icp-link">
                沪ICP备2026031193号-1
              </a>
            </div>
            <div className="login-icp-row">
              <a
                href="https://beian.mps.gov.cn/#/query/webSearch?code=31011502406967"
                target="_blank"
                rel="noopener noreferrer"
                className="login-icp-link"
              >
                <img
                  src="/assets/image/head-logo.png"
                  width="20"
                  height="22"
                  alt="公安备案图标"
                  className="login-beian-icon"
                />
                沪公网安备31011502406967号
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card" style={{ maxWidth: 440 }}>
        <div className="login-header">
          {loginLogo}
          <h1 className="login-title">小天书</h1>
          <p className="login-subtitle">个人写作工具</p>
        </div>

        <div style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.7,
          marginBottom: 24,
          padding: '12px 16px',
          backgroundColor: 'var(--hover-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: 8,
        }}>
          <p style={{ margin: '0 0 6px', fontWeight: 600, color: 'var(--text-color)' }}>声明</p>
          <p style={{ margin: '0 0 6px' }}>
            本网站为私人所有，仅供网站所有者个人使用，不向公众开放注册或提供任何公开发布服务。
          </p>
          <p style={{ margin: 0 }}>
            禁止任何形式的密码暴力破解或未授权访问尝试。
            如您非本网站所有者，请立即关闭本页面。
          </p>
        </div>

        {!hasPassword ? (
          <form onSubmit={handleSetPassword} className="login-form">
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, textAlign: 'center' }}>
              首次使用，请设置访问密码
            </p>
            <div className="form-group">
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="设置密码（至少4位）"
                className="form-input"
                autoFocus
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="确认密码"
                className="form-input"
                autoComplete="new-password"
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? '设置中...' : '设 置'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="login-form">
            <div className="form-group">
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="请输入访问密码"
                className="form-input"
                autoFocus
                autoComplete="off"
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? '验证中...' : '进 入'}
            </button>
          </form>
        )}

        <div className="login-icp">
          <div className="login-icp-row">
            <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer" className="login-icp-link">
              沪ICP备2026031193号-1
            </a>
          </div>
          <div className="login-icp-row">
            <a
              href="https://beian.mps.gov.cn/#/query/webSearch?code=31011502406967"
              target="_blank"
              rel="noopener noreferrer"
              className="login-icp-link"
            >
              <img
                src="/assets/image/head-logo.png"
                width="20"
                height="22"
                alt="公安备案图标"
                className="login-beian-icon"
              />
              沪公网安备31011502406967号
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
